import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { LegalSettingsSection } from "@/components/LegalSettingsSection";
import { DeletionLogSection } from "@/components/DeletionLogSection";
import { Users, CreditCard, Activity, FileText, BarChart3, Target, Gift, Loader2 } from "lucide-react";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/admin/payments").then((r) => setPayments(r.data));
  }, []);

  return (
    <div data-testid="admin-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kbar">Admin</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight mt-2">Panel administrador</h1>
        </div>
        <Link
          to="/admin/analytics"
          data-testid="admin-analytics-link"
          className="h-11 px-5 btn-brand inline-flex items-center gap-2 text-sm"
        >
          <BarChart3 className="w-4 h-4" /> Ver analítica
        </Link>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 mt-8">
          <Card icon={Users} label="Usuarios totales" value={stats.total_users} />
          <Card icon={Activity} label="Activos / Trial" value={`${stats.active} / ${stats.trialing}`} />
          <Card icon={FileText} label="Presupuestos" value={stats.total_budgets} />
          <Card icon={CreditCard} label="Ingresos" value={`${stats.revenue.toFixed(2)} €`} accent />
        </div>
      )}

      <h2 className="font-display font-bold text-2xl mt-12 tracking-tight">Usuarios</h2>
      <GrantFreeForeverForm onCreated={() => api.get("/admin/users").then((r) => setUsers(r.data))} />
      <div className="mt-4 border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <Th>Email</Th><Th>Nombre</Th><Th>Negocio</Th><Th>Estado</Th><Th>Trial fin</Th><Th>Creado</Th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100" data-testid={`admin-user-${u.id}`}>
                <Td>{u.email}</Td>
                <Td>{u.full_name}</Td>
                <Td>{u.business_name || "—"}</Td>
                <Td><span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 ${u.subscription_status === "active" ? "bg-green-100 text-green-700" : u.subscription_status === "trialing" ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-700"}`}>{u.subscription_status}</span></Td>
                <Td>{u.trial_ends_at ? u.trial_ends_at.slice(0, 10) : "—"}</Td>
                <Td>{u.created_at?.slice(0, 10)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="font-display font-bold text-2xl mt-12 tracking-tight">Cumplimiento legal</h2>
      <LegalSettingsSection />
      <DeletionLogSection />

      <h2 className="font-display font-bold text-2xl mt-12 tracking-tight">Pagos</h2>
      <div className="mt-4 border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200"><tr><Th>Fecha</Th><Th>User</Th><Th>Importe</Th><Th>Estado</Th></tr></thead>
          <tbody>
            {payments.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-slate-400">Aún no hay pagos.</td></tr>}
            {payments.map((p) => (
              <tr key={p.id} className="border-b border-slate-100">
                <Td>{p.created_at?.slice(0, 10)}</Td>
                <Td>{p.user_id}</Td>
                <Td>{p.amount?.toFixed(2)} {p.currency?.toUpperCase()}</Td>
                <Td>{p.status}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GrantFreeForeverForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "1234", full_name: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.includes("@") || form.password.length < 4) {
      toast.error("Email válido y contraseña >=4 caracteres");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users/grant-free-forever", form);
      toast.success(`${data.created ? "Creado" : "Actualizado"} · ${data.email} (PRO gratis)`);
      setForm({ email: "", password: "1234", full_name: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <div className="mt-3">
        <button
          onClick={() => setOpen(true)}
          data-testid="btn-grant-free-forever"
          className="h-10 px-4 border border-orange-300 text-orange-700 hover:bg-orange-50 inline-flex items-center gap-2 text-sm font-bold"
        >
          <Gift className="w-4 h-4" /> Crear cuenta gratuita PRO
        </button>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="mt-3 border border-orange-200 bg-orange-50 p-4" data-testid="grant-form">
      <div className="flex items-center gap-2 mb-3">
        <Gift className="w-4 h-4 text-orange-600" />
        <div className="font-bold text-sm">Nueva cuenta PRO gratuita y permanente</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input
          autoFocus
          required
          type="email"
          data-testid="grant-email"
          placeholder="email@ejemplo.com"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="h-10 px-3 border border-slate-200 text-sm outline-none focus:border-orange-600"
        />
        <input
          required
          data-testid="grant-password"
          placeholder="Contraseña"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          className="h-10 px-3 border border-slate-200 text-sm outline-none focus:border-orange-600"
        />
        <input
          data-testid="grant-name"
          placeholder="Nombre (opcional)"
          value={form.full_name}
          onChange={(e) => setForm({ ...form, full_name: e.target.value })}
          className="h-10 px-3 border border-slate-200 text-sm outline-none focus:border-orange-600"
        />
      </div>
      <div className="mt-3 flex gap-2">
        <button type="submit" disabled={busy} data-testid="grant-submit" className="h-10 px-4 bg-orange-600 text-white hover:bg-orange-700 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
          Crear / Actualizar
        </button>
        <button type="button" onClick={() => setOpen(false)} className="h-10 px-4 text-sm">Cancelar</button>
      </div>
      <p className="text-xs text-slate-600 mt-2">Si el email ya existe, se actualizará para conceder acceso PRO permanente.</p>
    </form>
  );
}

const Th = ({ children }) => <th className="text-left px-4 py-3 kbar">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-3">{children}</td>;

function Card({ icon: Icon, label, value, accent }) {
  return (
    <div className={`p-6 ${accent ? "bg-orange-600 text-white" : "bg-white"}`}>
      <Icon className={`w-6 h-6 ${accent ? "text-white/80" : "text-slate-400"}`} />
      <div className="mt-4 font-display font-extrabold text-3xl">{value}</div>
      <div className={`text-sm ${accent ? "text-white/90" : "text-slate-500"}`}>{label}</div>
    </div>
  );
}
